import React, { useState, useEffect } from 'react';
import { Store, Plus, Edit, Trash2, MapPin, Phone, X } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';

const AdminPOSLocations = () => {
  const { fetchWithAuth } = useAdminAuth();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    is_active: true
  });

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      const res = await fetchWithAuth('/api/admin/pos/locations');
      if (res.ok) {
        const data = await res.json();
        setLocations(data.locations || []);
      }
    } catch (error) {
      toast.error('Error al cargar puntos de venta');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      const res = editingLocation
        ? await fetchWithAuth(`/api/admin/pos/locations/${editingLocation.id}`, { method: 'PUT', body: formData })
        : await fetchWithAuth('/api/admin/pos/locations', { method: 'POST', body: formData });
      
      if (res.ok) {
        toast.success(editingLocation ? 'Punto de venta actualizado' : 'Punto de venta creado');
        setShowModal(false);
        setEditingLocation(null);
        resetForm();
        loadLocations();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Error');
      }
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const handleDelete = async (location) => {
    if (!window.confirm(`¿Eliminar "${location.name}"?`)) return;
    
    try {
      const res = await fetchWithAuth(`/api/admin/pos/locations/${location.id}`, { method: 'DELETE' });
      if (res.ok) {
        toast.success('Punto de venta eliminado');
        loadLocations();
      }
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const openEditModal = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address: location.address || '',
      phone: location.phone || '',
      is_active: location.is_active
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({ name: '', address: '', phone: '', is_active: true });
  };

  return (
    <div className="p-6" data-testid="admin-pos-locations">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Store className="text-amber-500" />
            Puntos de Venta
          </h1>
          <p className="text-gray-400 mt-1">Administra tus ubicaciones físicas de venta</p>
        </div>
        <button
          onClick={() => { setEditingLocation(null); resetForm(); setShowModal(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg transition-colors"
        >
          <Plus size={20} />
          Nueva Ubicación
        </button>
      </div>

      {/* Locations Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
        </div>
      ) : locations.length === 0 ? (
        <div className="bg-gray-800 rounded-xl p-12 text-center">
          <Store className="mx-auto mb-4 text-gray-600" size={60} />
          <h3 className="text-xl font-medium text-white mb-2">No hay puntos de venta</h3>
          <p className="text-gray-400 mb-4">Crea tu primer punto de venta para comenzar</p>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg transition-colors"
          >
            <Plus size={18} />
            Crear Punto de Venta
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {locations.map(location => (
            <div
              key={location.id}
              className={`bg-gray-800 rounded-xl p-5 border-2 transition-all ${
                location.is_active ? 'border-gray-700 hover:border-amber-500/50' : 'border-gray-700 opacity-60'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${location.is_active ? 'bg-amber-500/20 text-amber-500' : 'bg-gray-700 text-gray-500'}`}>
                    <Store size={24} />
                  </div>
                  <div>
                    <h3 className="font-medium text-white">{location.name}</h3>
                    {!location.is_active && (
                      <span className="text-xs text-gray-500">Inactivo</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEditModal(location)}
                    className="p-2 text-gray-400 hover:text-amber-500 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Editar"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(location)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {location.address && (
                <div className="flex items-start gap-2 text-sm text-gray-400 mb-2">
                  <MapPin size={14} className="mt-0.5 flex-shrink-0" />
                  <span>{location.address}</span>
                </div>
              )}

              {location.phone && (
                <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Phone size={14} />
                  <span>{location.phone}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl w-full max-w-md my-8 flex-shrink-0">
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h2 className="text-lg font-medium text-white">
                {editingLocation ? 'Editar Punto de Venta' : 'Nuevo Punto de Venta'}
              </h2>
              <button
                onClick={() => { setShowModal(false); setEditingLocation(null); }}
                className="text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-1">Nombre *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-amber-500"
                  placeholder="Ej: Tienda Centro"
                  required
                />
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-1">Dirección</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-amber-500"
                  placeholder="Calle, número, colonia..."
                />
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-1">Teléfono</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-amber-500"
                  placeholder="+52 123 456 7890"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-amber-500 bg-gray-700 border-gray-600 rounded focus:ring-amber-500"
                />
                <label htmlFor="is_active" className="text-sm text-gray-300">Activo</label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingLocation(null); }}
                  className="flex-1 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-amber-500 text-black font-medium rounded-lg hover:bg-amber-600 transition-colors"
                >
                  {editingLocation ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPOSLocations;
